import { useEffect, useRef, useState, useCallback } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Sparkles, User } from 'lucide-react';
import { sendMessage } from '../services/api';

gsap.registerPlugin(ScrollTrigger);

/* TypewriterText component - available for future use in AI response streaming */
// function TypewriterText({ text, onComplete }: { text: string; onComplete?: () => void }) {
//   ...implementation...
// }

export default function ChatTerminal() {
  const sectionRef = useRef<HTMLElement>(null);
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState<Array<{ role: 'user' | 'ai'; text: string }>>([]);
  const [isTyping, setIsTyping] = useState(false);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const sendBtnRef = useRef<HTMLButtonElement>(null);

  const scrollToBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    // Usa ajuste direto para evitar que alguns navegadores tentem ancorar o scroll da página.
    container.scrollTop = container.scrollHeight;
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Elastic button animation
  useEffect(() => {
    const btn = sendBtnRef.current;
    if (!btn) return;

    const handleEnter = () => {
      gsap.to(btn, { scale: 1.3, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
    };
    const handleLeave = () => {
      gsap.to(btn, { scale: 1, duration: 0.5, ease: 'elastic.out(1, 0.4)' });
    };

    btn.addEventListener('mouseenter', handleEnter);
    btn.addEventListener('mouseleave', handleLeave);
    return () => {
      btn.removeEventListener('mouseenter', handleEnter);
      btn.removeEventListener('mouseleave', handleLeave);
    };
  }, []);

  // Entrance animation
  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return;

    gsap.fromTo(
      '.chat-header',
      { opacity: 0, y: 30 },
      {
        opacity: 1, y: 0, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 70%', toggleActions: 'play none none none' },
      }
    );

    gsap.fromTo(
      '.chat-interface',
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 1, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 55%', toggleActions: 'play none none none' },
      }
    );
  }, []);

  const handleSend = useCallback(async () => {
    if (!input.trim() || isTyping) return;

    const lockedScrollY = window.scrollY;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { role: 'user', text: userMsg }]);
    setInput('');
    setIsTyping(true);

    try {
      const response = await sendMessage(userMsg);
      setMessages(prev => [...prev, { role: 'ai', text: response }]);
    } catch {
      setMessages(prev => [
        ...prev,
        { role: 'ai', text: 'A Vérixa está instável no momento. Tente novamente em instantes.' }
      ]);
    } finally {
      setIsTyping(false);
      // Restaura posição da página para evitar qualquer salto visual após envio.
      requestAnimationFrame(() => {
        window.scrollTo({ top: lockedScrollY, behavior: 'auto' });
      });
    }
  }, [input, isTyping]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSend();
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <section
      id="chat"
      ref={sectionRef}
      className="relative w-full py-32 px-6 overflow-hidden"
      style={{ background: '#050505', zIndex: 5 }}
    >
      {/* Background glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse 60% 50% at 50% 50%, rgba(0,240,255,0.04) 0%, transparent 70%)',
        }}
      />

      <div className="relative max-w-6xl mx-auto" style={{ zIndex: 2 }}>
        {/* Header */}
        <div className="chat-header text-center mb-16">
          <span
            className="text-[11px] uppercase tracking-[0.25em] font-semibold mb-4 block"
            style={{ color: '#00ff9c' }}
          >
            Terminal
          </span>
          <h2
            className="font-syncopate text-white uppercase"
            style={{
              fontSize: 'clamp(2rem, 4vw, 3rem)',
              textShadow: '0 0 40px rgba(0,255,156,0.15)',
            }}
          >
            Converse com a Vérixa
          </h2>
          <p className="mt-4 text-slate-400 max-w-lg mx-auto">
            Chat com respostas contextuais conectadas ao backend da Vérixa.
          </p>
          {/* Mode indicator */}
          <div className="flex justify-center mt-5">
            <span
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-pill text-[10px] uppercase tracking-[0.15em] font-semibold border"
              style={{
                background: 'rgba(0,255,156,0.06)',
                borderColor: 'rgba(0,255,156,0.25)',
                color: '#00ff9c',
              }}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#00ff9c] animate-pulse" />
              Modo Online
            </span>
          </div>
        </div>

        {/* Chat Interface */}
        <div className="chat-interface grid grid-cols-1 lg:grid-cols-5 gap-6 min-h-[500px]">
          {/* Left: Chat Area */}
          <div className="lg:col-span-3 flex flex-col rounded-panel overflow-hidden"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
            }}
          >
            {/* Messages */}
            <div
              ref={messagesContainerRef}
              className="flex-1 h-[400px] p-6 overflow-y-auto space-y-4"
              style={{ overflowAnchor: 'none' }}
            >
              {messages.length === 0 && (
                <div className="flex flex-col items-center justify-center h-full text-center py-16">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center mb-6 animate-float"
                    style={{
                      background: 'rgba(0,240,255,0.08)',
                      border: '1px solid rgba(0,240,255,0.2)',
                      boxShadow: '0 0 40px rgba(0,240,255,0.1)',
                    }}
                  >
                    <Sparkles className="w-7 h-7" style={{ color: '#00f0ff' }} />
                  </div>
                  <p className="text-slate-500 text-sm">
                    A Vérixa está esperando sua primeira mensagem...
                  </p>
                </div>
              )}

              {messages.map((msg, i) => (
                <div
                  key={i}
                  className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                >
                  {/* Avatar */}
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{
                      background: msg.role === 'user'
                        ? 'rgba(122,0,255,0.15)'
                        : 'rgba(0,240,255,0.08)',
                      border: `1px solid ${msg.role === 'user' ? 'rgba(122,0,255,0.3)' : 'rgba(0,240,255,0.2)'}`,
                    }}
                  >
                    {msg.role === 'user'
                      ? <User className="w-4 h-4 text-[#7a00ff]" />
                      : <Sparkles className="w-4 h-4 text-[#00f0ff]" />
                    }
                  </div>

                  {/* Bubble */}
                  <div
                    className={`max-w-[80%] p-4 rounded-2xl text-sm leading-relaxed whitespace-pre-line ${
                      msg.role === 'user'
                        ? 'bg-[#7a00ff]/15 text-white border border-[#7a00ff]/20'
                        : 'bg-[rgba(0,240,255,0.05)] text-slate-300 border border-[rgba(0,240,255,0.1)]'
                    }`}
                  >
                    {msg.text}
                  </div>
                </div>
              ))}

              {/* Typing indicator */}
              {isTyping && (
                <div className="flex gap-3">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      background: 'rgba(0,240,255,0.08)',
                      border: '1px solid rgba(0,240,255,0.2)',
                    }}
                  >
                    <Sparkles className="w-4 h-4 text-[#00f0ff] animate-pulse" />
                  </div>
                  <div
                    className="p-4 rounded-2xl border border-[rgba(0,240,255,0.1)]"
                    style={{ background: 'rgba(0,240,255,0.05)' }}
                  >
                    <div className="flex gap-1">
                      <span className="w-2 h-2 rounded-full bg-[#00f0ff] animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-2 h-2 rounded-full bg-[#00f0ff] animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-2 h-2 rounded-full bg-[#00f0ff] animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <p className="mt-2 text-xs text-slate-400">Vérixa digitando...</p>
                  </div>
                </div>
              )}

            </div>

            {/* Input */}
            <form
              onSubmit={handleSubmit}
              className="p-4 border-t border-white/5"
            >
              <div className="flex gap-3">
                <input
                  type="text"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleInputKeyDown}
                  placeholder="Fale com a Vérixa..."
                  className="flex-1 px-6 py-3 rounded-pill text-white text-sm outline-none transition-all duration-300 placeholder:text-slate-600"
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    boxShadow: input ? '0 0 20px rgba(0,240,255,0.1)' : 'none',
                  }}
                />
                <button
                  ref={sendBtnRef}
                  type="submit"
                  disabled={!input.trim() || isTyping}
                  className="w-12 h-12 rounded-full bg-white text-black flex items-center justify-center transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
            </form>
          </div>

          {/* Right: Visual Response Panel */}
          <div
            className="lg:col-span-2 rounded-panel p-8 flex flex-col items-center justify-center text-center relative overflow-hidden"
            style={{
              background: messages.length > 0
                ? 'linear-gradient(135deg, rgba(0,240,255,0.04), rgba(122,0,255,0.03))'
                : 'rgba(255,255,255,0.02)',
              border: messages.length > 0
                ? '1px solid rgba(0,240,255,0.12)'
                : '1px solid rgba(255,255,255,0.06)',
              transition: 'all 0.5s ease',
            }}
          >
            {/* Avatar Display */}
            <div
              className="relative w-32 h-32 mb-6"
              style={{
                opacity: messages.length > 0 ? 1 : 0.4,
                transition: 'opacity 0.5s ease',
              }}
            >
              <div
                className="absolute inset-0 rounded-full animate-pulse"
                style={{
                  background: 'radial-gradient(circle, rgba(0,240,255,0.15) 0%, transparent 70%)',
                  transform: 'scale(1.5)',
                }}
              />
              <img
                src="/assets/verixa-avatar.png"
                alt="Vérixa"
                className="w-full h-full object-contain relative z-10"
                style={{
                  filter: `drop-shadow(0 0 30px rgba(0,240,255,0.3))`,
                  animation: 'float 5s ease-in-out infinite',
                }}
              />
            </div>

            {/* Status */}
            <div className="space-y-2">
              <p className="font-syncopate text-xs uppercase tracking-[0.15em] text-white">
                Vérixa IA
              </p>
              <div className="flex items-center justify-center gap-2">
                <div
                  className="w-2 h-2 rounded-full animate-pulse"
                  style={{
                    background: isTyping ? '#00f0ff' : messages.length > 0 ? '#00ff9c' : '#94a3b8',
                    boxShadow: isTyping ? '0 0 10px #00f0ff' : messages.length > 0 ? '0 0 10px #00ff9c' : 'none',
                  }}
                />
                <span className="text-xs text-slate-500 uppercase tracking-[0.1em]">
                  {isTyping ? 'Digitando...' : messages.length > 0 ? 'Online' : 'Aguardando'}
                </span>
              </div>
            </div>

            {/* Particle burst effect on new message */}
            {messages.length > 0 && !isTyping && (
              <div
                className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-1"
                style={{
                  animation: 'particle-burst 1s ease-out forwards',
                }}
              >
                {[...Array(5)].map((_, i) => (
                  <div
                    key={i}
                    className="w-1 h-1 rounded-full"
                    style={{
                      background: '#00f0ff',
                      opacity: 0.6,
                      animationDelay: `${i * 0.1}s`,
                    }}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
